/**
 * Founder Control Center v2 · Sección 4 · Operations.
 *
 * Orden definitivo (V2 spec §5.1 + Fase 5 Sprint Q, lista del founder):
 *   1. Centros (orgs + directorio fusionados) → CentrosUnified (FO)            ✅ migrado Fase 5
 *   2. Leads (platform-wide inbox)            → LeadsAdminPanel (FO · NUEVO)   ✅ migrado Fase 5
 *   3. Reservas (overview agregado)           → ReservasOverview (FO · NUEVO)  ✅ migrado Fase 5
 *   4. Marketplace (overview público)         → MarketplaceOverview (FO · NUEVO) ✅ migrado Fase 5
 *   5. Productos (ex Catálogo)                → SuperAdmin tab "catalog" (embed) · 🟡 SA-dep
 *   6. Proveedores                            → ProveedoresOverview (FO · NUEVO) ✅ migrado Fase 5
 *   7. Pricing                                → SuperAdmin tab "pricing" (embed) · 🟡 SA-dep
 *   8. Facturas SaaS                          → SuperAdmin tab "invoices" (embed) · 🟡 SA-dep
 *   9. Roadmap                                → SuperAdmin tab "roadmap" (embed) · 🟡 SA-dep
 *  10. Brand                                  → SuperAdmin tab "brand" (embed) · 🟡 SA-dep
 *  11. Tools / QR                             → SuperAdmin tab "qr" (embed) · 🟡 SA-dep
 *
 * 🟡 SA-dep = dependencia temporal de SuperAdmin · pendiente extracción en Fases 7-8.
 *
 * Cambios respecto a Fase 2:
 *   - Centros + Directorio FUSIONADOS (V2 §5.2): 2 sub-tabs anteriores → 1 sub-tab con A/B interno.
 *   - NUEVOS sub-tabs: Leads, Reservas, Marketplace, Proveedores (componentes
 *     dedicados read+limited-write reutilizando endpoints existentes).
 *   - Productos: re-etiquetado claro (antes "Catálogo"). Endpoint y componente intactos.
 *   - 0 backend tocado · 0 endpoints nuevos · 0 mocks introducidos.
 *
 * Compatibilidad legacy:
 *   - Cualquier deep-link previo `?sub=directorio` se redirige a `?sub=centros` (interno B).
 *   - Cualquier deep-link previo `?sub=catalog` se redirige a `?sub=productos`.
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Buildings, Tray, CalendarBlank, Storefront, Stack, Truck,
  CurrencyEur, Receipt, Sparkle, QrCode, Wrench, MapPin,
} from "@phosphor-icons/react";
import SuperAdmin from "../../SuperAdmin";
import CentrosUnified from "../CentrosUnified";
import LeadsAdminPanel from "../LeadsAdminPanel";
import ReservasOverview from "../ReservasOverview";
import MarketplaceOverview from "../MarketplaceOverview";
import ProveedoresOverview from "../ProveedoresOverview";
import SubTabs, { useSubTab } from "./SubTabs";

const ITEMS = [
  { key: "centros",      label: "Centros",        icon: Buildings },
  { key: "leads",        label: "Leads",          icon: Tray },
  { key: "reservas",     label: "Reservas",       icon: CalendarBlank },
  { key: "marketplace",  label: "Marketplace",    icon: Storefront },
  { key: "productos",    label: "Productos",      icon: Stack },
  { key: "proveedores",  label: "Proveedores",    icon: Truck },
  { key: "pricing",      label: "Pricing",        icon: CurrencyEur },
  { key: "invoices",     label: "Facturas SaaS",  icon: Receipt },
  { key: "roadmap",      label: "Roadmap",        icon: MapPin },
  { key: "brand",        label: "Brand",          icon: Sparkle },
  { key: "qr",           label: "Tools / QR",     icon: QrCode },
];

// Redirects internos para deep-links legacy.
const LEGACY_ALIAS = {
  directorio: "centros",  // ahora interno A/B dentro de Centros
  catalog:    "productos",
  tools:      "qr",
};

export default function OperationsSection() {
  const [sub, setSub] = useSubTab("centros");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Redirect deep-links legacy a la nueva nomenclatura
  useEffect(() => {
    const current = searchParams.get("sub");
    if (current && LEGACY_ALIAS[current]) {
      const next = new URLSearchParams(searchParams);
      next.set("sub", LEGACY_ALIAS[current]);
      navigate(`/founder/operations?${next.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="fcc-operations-section">
      <Header />
      <SubTabs items={ITEMS} value={sub} onChange={setSub} testid="operations-subtabs" />
      <div className="pt-2">
        {sub === "centros"     && <CentrosUnified />}
        {sub === "leads"       && <LeadsAdminPanel />}
        {sub === "reservas"    && <ReservasOverview />}
        {sub === "marketplace" && <MarketplaceOverview />}
        {sub === "productos"   && <SuperAdmin embedded forceTab="catalog" />}
        {sub === "proveedores" && <ProveedoresOverview />}
        {sub === "pricing"     && <SuperAdmin embedded forceTab="pricing" />}
        {sub === "invoices"    && <SuperAdmin embedded forceTab="invoices" />}
        {sub === "roadmap"     && <SuperAdmin embedded forceTab="roadmap" />}
        {sub === "brand"       && <SuperAdmin embedded forceTab="brand" />}
        {sub === "qr"          && <SuperAdmin embedded forceTab="qr" />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
        <Wrench size={11} weight="fill" /> Founder Control Center · Operations
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
        Gestiona la plataforma
      </h1>
      <p className="text-xs text-[var(--ag-text-dim)] mt-1">
        Centros · Leads · Reservas · Marketplace · Productos · Proveedores · Pricing · Facturas · Roadmap · Brand · Tools
      </p>
    </div>
  );
}
